/**
 * Utility functions for hostname and admin mode detection.
 *
 * Production hosts are configured via REACT_APP_ADMIN_HOSTNAMES (comma
 * separated). On localhost, admin mode is tracked in sessionStorage and is
 * set/cleared from the `?admin=true` / `?admin=false` query parameter.
 */

const ADMIN_MODE_STORAGE_KEY = 'budgit:adminMode';
const DEFAULT_MAIN_APP_URL = 'https://budgit.app';
const DEFAULT_ADMIN_URL = 'https://admin.budgit.app';
const DEFAULT_ADMIN_HOSTNAMES = 'admin.budgit.app,admin.localhost';

const getAdminHostnames = () => {
  const raw = process.env.REACT_APP_ADMIN_HOSTNAMES || DEFAULT_ADMIN_HOSTNAMES;
  return raw
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
};

const isLocalHostname = (hostname) =>
  hostname === 'localhost' || hostname === '127.0.0.1';

/**
 * Sync the admin-mode sessionStorage flag from the `?admin` query parameter.
 * Should be called once at app boot. Idempotent and safe in environments
 * where sessionStorage is unavailable.
 */
export const syncAdminModeFromQuery = () => {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const adminParam = params.get('admin');
    if (adminParam === 'true') {
      window.sessionStorage.setItem(ADMIN_MODE_STORAGE_KEY, 'true');
    } else if (adminParam === 'false') {
      window.sessionStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
    }
  } catch (_) {
    // sessionStorage may be unavailable (private mode, SSR)
  }
};

/**
 * Detect whether the current page is the admin portal.
 *
 * On production-style hostnames the check is purely host-based (compared
 * against REACT_APP_ADMIN_HOSTNAMES). On localhost the check reads a
 * sessionStorage flag that is set by `?admin=true`.
 */
export const isAdminHost = () => {
  if (typeof window === 'undefined') return false;
  const hostname = (window.location.hostname || '').toLowerCase();
  if (getAdminHostnames().includes(hostname)) return true;
  if (isLocalHostname(hostname)) {
    try {
      return window.sessionStorage.getItem(ADMIN_MODE_STORAGE_KEY) === 'true';
    } catch (_) {
      return false;
    }
  }
  return false;
};

/** Backwards-compatible alias. */
export const isAdminMode = isAdminHost;

/**
 * Explicitly clear the admin-mode sessionStorage flag (e.g. on logout).
 * Without this, the flag would leak across users sharing a tab.
 */
export const clearAdminMode = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
  } catch (_) {
    // sessionStorage may be unavailable (private mode, SSR)
  }
};

/**
 * URL of the main (non-admin) Budgit web app. On localhost this returns the
 * current origin with `?admin=false` appended so that the page reload
 * triggered by the sidebar link clears the sessionStorage admin flag (mirror
 * of getAdminUrl which appends `?admin=true`).
 */
export const getMainAppUrl = () => {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_MAIN_APP_URL || DEFAULT_MAIN_APP_URL;
  }
  if (isLocalHostname(window.location.hostname)) {
    return `${window.location.origin}?admin=false`;
  }
  return process.env.REACT_APP_MAIN_APP_URL || DEFAULT_MAIN_APP_URL;
};

/**
 * URL of the admin portal. On localhost this returns the current origin with
 * `?admin=true` appended so the same dev server can serve both apps.
 */
export const getAdminUrl = () => {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_ADMIN_URL || DEFAULT_ADMIN_URL;
  }
  if (isLocalHostname(window.location.hostname)) {
    return `${window.location.origin}?admin=true`;
  }
  return process.env.REACT_APP_ADMIN_URL || DEFAULT_ADMIN_URL;
};
